import React from "react";
import { Link } from "react-router-dom";
import { CustomerServiceSidebar } from "./Delivery";
import "../css/content-page.scss";
import "../css/privacy.scss";

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

function Privacy() {
  return (
    <main className="content-page privacy-page">
      <div className="content-page__shell">
        <CustomerServiceSidebar currentPath="/privacy" />

        <article
          className="content-page__article"
          aria-labelledby="privacy-title"
        >
          <header className="content-page__header">
            <p className="content-page__eyebrow">Your information and choices</p>
            <h1 id="privacy-title">Privacy Policy</h1>
            <p className="content-page__lead">
              This page explains information handled by sickzuno when you browse,
              use the cart or continue through checkout.
            </p>
          </header>

          <p className="content-page__meta">Last updated: September 30, 2026</p>

          <div
            className="content-page__notice privacy-page__template-note"
            role="note"
          >
            <strong>Privacy first:</strong> card details are not collected by this
            page. Payment information is entered only on the secure payment page.
          </div>

          <section
            className="content-page__section"
            aria-labelledby="privacy-scope"
          >
            <h2 id="privacy-scope">1. Scope of this policy</h2>
            <p>
              This notice covers browsing, the shopping cart, customer contact
              features and checkout on the sickzuno storefront.
            </p>
            <p>
              Service providers may apply their own privacy terms when a visitor
              follows a secure payment or delivery-service link.
            </p>
          </section>

          <section
            className="content-page__section"
            aria-labelledby="privacy-information"
          >
            <h2 id="privacy-information">2. Information we handle</h2>
            <div className="content-page__table-wrap">
              <table className="content-page__table">
                <caption className="content-page__visually-hidden">
                  Information handled by the storefront
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Feature</th>
                    <th scope="col">Information</th>
                    <th scope="col">Current behavior</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Shopping cart</td>
                    <td>
                      Product ID, selected variant, packaging and quantity
                    </td>
                    <td>
                      Stored locally in this browser under the key “cartItems”.
                    </td>
                  </tr>
                  <tr>
                    <td>Contact form</td>
                    <td>Name, email, subject and message</td>
                    <td>
                      Held temporarily in page memory and cleared after the
                      on-screen confirmation.
                    </td>
                  </tr>
                  <tr>
                    <td>Checkout</td>
                    <td>
                      Contact and delivery-address fields entered by the visitor
                    </td>
                    <td>
                      Used to prepare the order and initiate payment. Card details
                      are not requested or stored by this form.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section
            className="content-page__section"
            aria-labelledby="privacy-use"
          >
            <h2 id="privacy-use">3. How that information is used</h2>
            <ul className="content-page__list">
              <li>
                Cart data restores selected products when the same browser
                returns.
              </li>
              <li>
                Contact-form data supports validation and an on-screen submission
                confirmation.
              </li>
              <li>
                Checkout values support validation, order creation and payment
                status confirmation.
              </li>
              <li>
                The current project code does not use this information for
                advertising, profiling or sale to third parties.
              </li>
            </ul>
          </section>

          <section
            className="content-page__section"
            aria-labelledby="privacy-storage"
          >
            <h2 id="privacy-storage">4. Browser storage and your choices</h2>
            <p>
              The cart uses local storage, which normally remains until it is
              removed. You can remove cart records by emptying the cart or
              clearing this site’s stored data through browser settings.
              Disabling browser storage may prevent the cart from remaining
              available between visits.
            </p>
            <p>
              No application-specific analytics or advertising cookie is
              configured in the project code covered by this template. A future
              hosting platform or added service may have separate logging or
              cookie practices that would need to be disclosed here.
            </p>
          </section>

          <section
            className="content-page__section"
            aria-labelledby="privacy-sharing"
          >
            <h2 id="privacy-sharing">5. Sharing and retention</h2>
            <p>
              Cart information remains in the visitor’s browser. Checkout sends
              the order reference, total, currency, email and first product title
              to the site payment endpoint so the payment can be initiated.
            </p>
          </section>

          <section
            className="content-page__section"
            aria-labelledby="privacy-safety"
          >
            <h2 id="privacy-safety">6. Security and children</h2>
            <p>
              Browser storage is convenient, but it should not be used for
              passwords, payment-card details or other sensitive information.
              This storefront is intended for a general shopping audience and is not
              designed to knowingly collect personal information from children.
            </p>
          </section>

          <section
            className="content-page__section"
            aria-labelledby="privacy-updates"
          >
            <h2 id="privacy-updates">7. Changes and questions</h2>
            <p>
              The “Last updated” date should change whenever this template
              changes. Questions about this notice can be sent through the
              on-site contact form. Use of the storefront is also subject to the{" "}
              <Link
                className="content-page__inline-link"
                to="/terms"
                onClick={scrollToTop}
              >
                Terms &amp; Conditions
              </Link>
              .
            </p>
          </section>

          <aside className="content-page__support" aria-label="Privacy support">
            <div>
              <h2>Have a privacy question?</h2>
              <p>
                Use the local contact page to describe what you would like
                clarified.
              </p>
            </div>
            <Link
              className="content-page__inline-link"
              to="/contact"
              onClick={scrollToTop}
            >
              Contact sickzuno
            </Link>
          </aside>
        </article>
      </div>
    </main>
  );
}

export default Privacy;
